<h3 class="with-manual-link">
  <span i18n="@@configuration">Konfiguration</span>
  <app-manual-link href="/Handbuch/Nutzeroberfläche/Bot-Verwaltung/Allgemein/Konfiguration"></app-manual-link>
</h3>

<app-submenu [items]="this.submenuService.SUBMENU_FOR_CONFIGURATION"></app-submenu>

<div class="d-flex flex-column gap-3 mt-2">
  <h3
    i18n="@@config-heading-ai-params"
    class="mt-4"
  >KI-Parameter</h3>

  <div class="row align-items-center">
    <div class="col-3">
      <span i18n="@@model-provider">Anbieter</span>
    </div>
    <div class="col-auto">
      <p-select
        [options]="modelSources"
        [(ngModel)]="this.rss.botConfig.modelSource"
        [ngModelOptions]="{ updateOn: 'blur' }"
        (ngModelChange)="this.updateModelSource()"
      ></p-select>
    </div>
  </div>

  <div class="row align-items-center">
    <div class="col-3">
      <span i18n="@@model">Model</span>
    </div>
    <div class="col-auto">
      <p-select
        [options]="availableModels"
        [(ngModel)]="this.rss.botConfig.model"
        [ngModelOptions]="{ updateOn: 'blur' }"
        [disabled]="this.rss.botConfig.modelSource === 'vllm'"
        [filter]="true"
        filterPlaceholder="Suchen"
        i18n-filterPlaceholder="@@model-select-filter-placeholder"
        styleClass="model-select"
        (ngModelChange)="this.rss.saveBotConfigField('model'); this.checkReasoning(); this.checkWebSearch()"
      ></p-select>
    </div>
  </div>

  <div class="row align-items-center">
    <div class="col-3">
      <span i18n="@@text-verbosity">Textausführlichkeit</span>
      <app-hint i18n="@@text-verbosity-hint">Textausführlichkeit bestimmt, wie ausführlich und detailliert die Antworten
        des Bots formuliert werden. Ein niedriger Wert sorgt für kurze, prägnante Antworten, ein höherer Wert für
        ausführlichere Erklärungen und mehr Kontext.</app-hint>
    </div>
    <div class="col-auto">
      <p-select
        [options]="textVerbosityOptions"
        optionLabel="name"
        optionValue="value"
        [(ngModel)]="this.rss.botConfig.textVerbosity"
        [ngModelOptions]="{ updateOn: 'blur' }"
        (ngModelChange)="this.rss.saveBotConfigField('textVerbosity');"
        [disabled]="!isReasoningModel(this.rss.botConfig.model)"
      ></p-select>
    </div>
  </div>

  <div class="row align-items-center">
    <div class="col-3">
      <span i18n="@@temperature">Temperatur</span>
      @if (isReasoningModel(this.rss.botConfig.model)) {
      <app-hint i18n="@@temperature-models-hint">
        Die Temperatur lässt sich bei GPT-5 und GPT-6 Astra Modellen nicht einstellen
      </app-hint>
      }
    </div>

    <div class="col lock-sensitive">
      <div class="temperature-slider">
        <span
          class="temperature-label"
          i18n="@@temperature-precise"
        >Präzise</span>
        <div class="temperature-slider-wrapper">
          <div
            class="temperature-value-bubble"
            [style.margin-left.%]="this.rss.botConfig.temperature * 100"
          >
            {{ this.rss.botConfig.temperature | number:'1.1-1':'de-DE' }}
          </div>
          <p-slider
            [(ngModel)]="this.rss.botConfig.temperature"
            [min]="0"
            [max]="1"
            [step]="0.1"
            (onSlideEnd)="this.saveTemperature()"
            [disabled]="isReasoningModel(this.rss.botConfig.model)"
          ></p-slider>
          <div class="temperature-scale">
            <span>0</span>
            <span>0,5</span>
            <span>1</span>
          </div>
        </div>
        <span
          class="temperature-label"
          i18n="@@temperature-creative"
        >Kreativ</span>
      </div>
    </div>
  </div>

  <div class="row align-items-center">
    <div class="col-3">
      <span i18n="@@speech-recognition">Spracherkennung</span>
      <app-hint i18n="@@speech-recognition-hint">Sprache für die Umwandlung von Sprache in Text </app-hint>
    </div>

    <div class="col-auto">
      <p-select
        [options]="speechRecognitionOptions"
        [(ngModel)]="this.rss.botConfig.speechRecognitionMode"
        optionLabel="label"
        optionValue="value"
        [ngModelOptions]="{ updateOn: 'blur' }"
        (ngModelChange)="this.rss.saveBotConfigField('speechRecognitionMode')"
      ></p-select>
    </div>
  </div>

  <h3
    i18n="@@config-heading-appearance"
    class="mt-4"
  >Erscheinungsbild</h3>

  <div class="row align-items-start">
    <div class="col-3">
      <span i18n="@@bot-style">Bot-Bild</span>
    </div>

    <div class="col-auto">
      <div class="d-flex flex-column gap-2">
        <div class="d-flex gap-2">
          @if (!this.rss.botConfig.hasImage) {
          <div>
            <p-select
              [options]="iconOptions"
              [(ngModel)]="this.rss.botConfig.icon"
              optionLabel="label"
              optionValue="value"
              (onChange)="this.rss.saveBotConfigField('icon')"
            >
              <ng-template
                let-option
                pTemplate="item"
              >
                <div class="d-flex gap-1 align-items-center w-100">
                  <span i18n="@@bot-icon">Icon:</span>
                  <i [class]="option.value"></i>
                </div>
              </ng-template>
              <ng-template
                let-selectedOption
                pTemplate="selectedItem"
              >
                <div class="d-flex gap-1 align-items-center w-100">
                  <span i18n="@@bot-icon">Icon:</span>
                  <i [class]="selectedOption?.value ?? ''"></i>
                </div>
              </ng-template>
            </p-select>
          </div>
          }

          <div>
            <p-select
              [options]="colorOptions"
              [(ngModel)]="this.rss.botConfig.color"
              optionLabel="label"
              optionValue="value"
              (onChange)="this.rss.saveBotConfigField('color')"
            >
              <ng-template
                let-option
                pTemplate="item"
              >
                <div class="d-flex gap-1 align-items-center w-100">
                  @if (option.value === 'transparent') {
                  <span class="color-swatch color-swatch-transparent"></span>
                  <span i18n="@@bot-color-transparent">Transparent</span>
                  } @else {
                  <span i18n="@@bot-color">Farbe:</span>
                  <span class="d-flex align-items-center">
                    <i
                      class="ri-circle-fill"
                      [ngStyle]="{ color: option.value }"
                    ></i>
                    <span>{{ option.value }}</span>
                  </span>
                  }
                </div>
              </ng-template>

              <ng-template
                let-selectedOption
                pTemplate="selectedItem"
              >
                <div class="d-flex gap-1 align-items-center w-100">
                  @if ((selectedOption?.value ?? this.rss.botConfig.color) === 'transparent') {
                  <span class="color-swatch color-swatch-transparent"></span>
                  <span i18n="@@bot-color-transparent">Transparent</span>
                  } @else {
                  <span i18n="@@bot-color">Farbe:</span>
                  <span class="d-flex align-items-center">
                    <i
                      class="ri-circle-fill"
                      [ngStyle]="{ color: selectedOption?.value ?? this.rss.botConfig.color }"
                    ></i>
                    <span>{{ selectedOption?.value ?? this.rss.botConfig.color }}</span>
                  </span>
                  }
                </div>
              </ng-template>

              <ng-template pTemplate="footer">
                <div class="p-2 border-top">
                  <input
                    type="color"
                    class="form-control form-control-sm"
                    [(ngModel)]="this.rss.botConfig.color"
                    [ngModelOptions]="{ updateOn: 'blur' }"
                    (ngModelChange)="this.rss.saveBotConfigField('color')"
                  >
                </div>
              </ng-template>
            </p-select>
          </div>
        </div>

        <div class="d-flex align-items-center gap-3">
          <div
            class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style="width: 56px; height: 56px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);"
            [ngStyle]="{
              'background-color': this.rss.botConfig.hasImage ? 'transparent' : this.rss.botConfig.color,
              'background-image': this.rss.botConfig.hasImage ? 'url(/api/toolbox/membot/public/botimg/' + this.rss.botConfig._id + '/128?t=' + this.rss.botImageCacheBuster + ')' : 'none',
              'background-size': 'cover',
              'background-position': 'center',
              'color': this.getContrastColor(this.rss.botConfig.color)
            }"
          >
            @if (!this.rss.botConfig.hasImage) {
            <i
              [class]="this.rss.botConfig.icon"
              style="font-size: 24px;"
            ></i>
            }
          </div>

          <app-bot-image-reload></app-bot-image-reload>
        </div>
      </div>
    </div>
  </div>

  @if (!this.showOpenImageConfig) {
  <div class="row align-items-center">
    <div class="col-3"></div>
    <div class="col-auto">
      <p-button
        severity="secondary"
        [text]="true"
        icon="ri-add-line"
        (onClick)="this.revealOpenImageConfig()"
        i18n-label="@@bot-style-add-open-image"
        label="Zusätzliches Bild zum Schließen des Bots hinzufügen"
      ></p-button>
    </div>
  </div>
  }

  @if (this.showOpenImageConfig) {
  <div class="row align-items-start">
    <div class="col-3">
      <span i18n="@@bot-style-open-heading">Bot-Bild zum Schließen</span>
      <app-hint i18n="@@bot-style-open-hint">
        Wird im Fusion-View-Button gezeigt, sobald der Bot geöffnet ist, damit erkennbar ist, dass ein Klick den Bot
        schließt. Ohne eigenes Bild wird automatisch das Bild oben verwendet.
      </app-hint>
    </div>

    <div class="col-auto">
      <div class="d-flex align-items-center gap-3">
        <div
          class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style="width: 56px; height: 56px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);"
          [ngStyle]="{
            'background-color': this.rss.botConfig.hasImageOpen ? 'transparent' : this.rss.botConfig.color,
            'background-image': this.rss.botConfig.hasImageOpen ? 'url(/api/toolbox/membot/public/botimg/' + this.rss.botConfig._id + '/128?variant=open&t=' + this.rss.botImageCacheBuster + ')' : 'none',
            'background-size': 'cover',
            'background-position': 'center',
            'color': this.getContrastColor(this.rss.botConfig.color)
          }"
        >
          @if (!this.rss.botConfig.hasImageOpen) {
          <i
            [class]="this.rss.botConfig.icon"
            style="font-size: 24px;"
          ></i>
          }
        </div>

        <app-bot-image-reload variant="open"></app-bot-image-reload>
      </div>
    </div>
  </div>
  }

  <div class="row align-items-center mt-4">
    <div class="col-3">
      <span i18n="@@loadingmessages">Ladenachrichten</span>
      <app-hint i18n="@@loadingmesages-hint">
        <b> Eine Nachricht pro Zeile</b>
      </app-hint>
    </div>

    <div class="col-auto">
      <p-button
        severity="secondary"
        [outlined]="true"
        [icon]="loadingMessagesOpen ? 'ri-eye-off-line' : 'ri-eye-line'"
        (onClick)="loadingMessagesOpen = !loadingMessagesOpen"
        [label]="loadingMessagesOpen ? 'Ausblenden' : 'Ladenachrichten anzeigen'"
      ></p-button>
    </div>
  </div>

  @if (loadingMessagesOpen) {
  <div class="row">
    <div class="col-3"></div>
    <div class="col">
      <textarea
        pInputTextarea
        class="w-100 border-2"
        rows="9"
        [(ngModel)]="this.rss.botConfig.loadingMessages"
        (ngModelChange)="this.rss.saveBotConfigField('loadingMessages')"
        [ngModelOptions]="{ updateOn: 'blur' }"
      ></textarea>
    </div>
  </div>
  }

  <div class="d-flex flex-column gap-1 mt-2">
    <div class="row align-items-center">
      <div class="col-3">
        <span i18n="@@custom-css">Globaler CSS-Code</span>
      </div>

      <div class="col-auto">
        <p-button
          severity="secondary"
          [outlined]="true"
          [icon]="customCssOpen ? 'ri-eye-off-line' : 'ri-code-s-slash-line'"
          (onClick)="customCssOpen = !customCssOpen"
          [label]="customCssOpen ? 'Editor ausblenden' : 'Editor einblenden'"
        ></p-button>
      </div>
    </div>

    @if (customCssOpen) {
    <app-codemirror-css
      [instruction]="this.rss.botConfig.customCss"
      [rows]="13"
      [botlyContext]="'Global'"
      [placeholder]="this.enterCustomCss"
      (finish)="this.rss.botConfig.customCss = $event; this.rss.saveBotConfigField('customCss')"
    ></app-codemirror-css>
    }
  </div>
</div>

<div class="d-flex flex-column gap-2 mt-5">
  <div class="d-flex flex-column gap-1">
    <div i18n="@@respawn-behavior">
      <h3 class="mt-4">Wiedereinstiegs-Optionen</h3>
    </div>
    <app-form-for-boolean
      [label]="this.clearChatDescription"
      [(value)]="this.rss.botConfig.createEmptyThreadOnVisit"
      [colClass]="'col-3'"
      (valueChange)="this.rss.saveBotConfigField('createEmptyThreadOnVisit')"
    >
      <app-hint i18n="@@clear-chat-hint">
        Wenn ein Nutzer alle geöffneten Browserfenster zu einem Bot schließt, gilt der Chat nach wenigen Sekunden als
        beendet. Beim nächsten Öffnen des Bots startet der Chat ohne vorherigen Verlauf.
      </app-hint>
    </app-form-for-boolean>
    @if (this.rss.botConfig.createEmptyThreadOnVisit) {
    <div class="row mt-2">
      <div class="col-3">
        <span i18n="@@clear-chat-time">Verzögerung</span>
      </div>
      <div class="col">
        <div class="row">
          <div class="col-auto">
            <input
              pInputText
              type="number"
              style="width: 80px;"
              min="1"
              [(ngModel)]="this.rss.botConfig.secondsToWaitForThreadDeleting"
              [ngModelOptions]="{updateOn: 'blur'}"
              (ngModelChange)="this.saveAutoDeleteTime()"
            >
          </div>
        </div>
      </div>
    </div>
    }
  </div>

  <div class="d-flex flex-column gap-1 mt-4">
    <h5 i18n="@@custom-js">
      Globaler Javascript-Code
    </h5>

    <p-button
      severity="secondary"
      [outlined]="true"
      [gotoBlockly]="this.rss.editorItems.global"
      i18n-label="@@to-scripting"
      label="Zum Scripting"
    ></p-button>
  </div>

</div>